/**
 * LocalSecretsProvider — AES-256-GCM encrypted file-blob storage at `~/.skaile/secrets/`.
 *
 * Each secret is stored as a separate `.enc` file named by the SHA-256 hash of the ref
 * (collision-free, filesystem-safe). A companion `.meta.json` stores the original ref,
 * timestamps, and optional user metadata. The master key is derived via PBKDF2-SHA-512
 * from one of three {@link KeyStrategy} sources: OS keychain (keytar), a passphrase from
 * `SKAILE_SECRETS_PASSPHRASE`, or a machine-derived passphrase as last resort.
 * Switching strategies re-encrypts all existing secrets with the new key.
 *
 * @docLink packages/secrets/concepts#local-secrets-provider
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";

import type {
  ISecretsProvider,
  ListFilter,
  ProviderCapabilities,
  SecretMetadata,
  SetSecretOptions,
} from "@skaile/workspaces/plugins";
import {
  ProviderUnavailableError,
  SecretNotFoundError,
  SecretsError,
} from "@skaile/workspaces/plugins";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALGORITHM = "aes-256-gcm";
const IV_LENGTH = 12;
const AUTH_TAG_LENGTH = 16;
const SALT_LENGTH = 32;
const KEY_LENGTH = 32;
const PBKDF2_ITERATIONS = 100_000;
const PBKDF2_DIGEST = "sha512";

const KEYCHAIN_SERVICE = "skaile-secrets";
const KEYCHAIN_ACCOUNT = "master-key";

/** Env var that provides the passphrase at runtime (never stored on disk). */
const PASSPHRASE_ENV = "SKAILE_SECRETS_PASSPHRASE";

/**
 * Master key derivation strategy used by {@link LocalSecretsProvider}.
 *
 * - `keychain` — OS keychain (via keytar); most secure, requires keytar to be installed.
 * - `passphrase` — PBKDF2 from `SKAILE_SECRETS_PASSPHRASE` env var; portable across machines.
 * - `machine` — deterministic from hostname + username; least secure, last-resort fallback.
 *
 * The active strategy is persisted in `~/.skaile/secrets/.key-strategy`. Switching strategies
 * re-encrypts all existing secrets with the new key.
 *
 * @docLink packages/secrets/concepts#local-secrets-provider
 */
export type KeyStrategy = "keychain" | "passphrase" | "machine";

// ---------------------------------------------------------------------------
// Key derivation
// ---------------------------------------------------------------------------

function deriveMasterKey(passphrase: string, salt: Buffer): Buffer {
  return crypto.pbkdf2Sync(passphrase, salt, PBKDF2_ITERATIONS, KEY_LENGTH, PBKDF2_DIGEST);
}

function getMachinePassphrase(): string {
  // Deterministic but not secret -- last resort only.
  return `skaile:${os.hostname()}:${os.userInfo().username}`;
}

/** Hash a passphrase for verification (stored in .master). */
function hashPassphrase(passphrase: string): string {
  return crypto.createHash("sha256").update(passphrase).digest("hex");
}

// ---------------------------------------------------------------------------
// Filename hashing (injective: sha256 of ref)
// ---------------------------------------------------------------------------

function refToHash(ref: string): string {
  return crypto.createHash("sha256").update(ref).digest("hex").slice(0, 40);
}

// ---------------------------------------------------------------------------
// Encryption / decryption
// ---------------------------------------------------------------------------

function encrypt(data: Uint8Array, key: Buffer): Buffer {
  const iv = crypto.randomBytes(IV_LENGTH);
  const cipher = crypto.createCipheriv(ALGORITHM, key, iv, { authTagLength: AUTH_TAG_LENGTH });
  const encrypted = Buffer.concat([cipher.update(data), cipher.final()]);
  const authTag = cipher.getAuthTag();
  // Format: [iv (12)] [authTag (16)] [ciphertext (...)]
  return Buffer.concat([iv, authTag, encrypted]);
}

function decrypt(blob: Buffer, key: Buffer): Buffer {
  const iv = blob.subarray(0, IV_LENGTH);
  const authTag = blob.subarray(IV_LENGTH, IV_LENGTH + AUTH_TAG_LENGTH);
  const ciphertext = blob.subarray(IV_LENGTH + AUTH_TAG_LENGTH);
  const decipher = crypto.createDecipheriv(ALGORITHM, key, iv, { authTagLength: AUTH_TAG_LENGTH });
  decipher.setAuthTag(authTag);
  return Buffer.concat([decipher.update(ciphertext), decipher.final()]);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export class LocalSecretsProvider implements ISecretsProvider {
  readonly id = "local";
  private secretsDir: string;
  private masterKey: Buffer | null = null;
  private salt: Buffer | null = null;
  private strategy: KeyStrategy = "machine";

  constructor(secretsDir?: string) {
    this.secretsDir = secretsDir ?? path.join(os.homedir(), ".skaile", "secrets");
  }

  // -- Initialization --------------------------------------------------------

  private ensureDir(): void {
    if (!fs.existsSync(this.secretsDir)) {
      fs.mkdirSync(this.secretsDir, { recursive: true, mode: 0o700 });
    }
  }

  private loadOrCreateSalt(): Buffer {
    const saltPath = path.join(this.secretsDir, ".salt");
    if (fs.existsSync(saltPath)) {
      return fs.readFileSync(saltPath);
    }
    const salt = crypto.randomBytes(SALT_LENGTH);
    fs.writeFileSync(saltPath, salt, { mode: 0o600 });
    return salt;
  }

  private async ensureKey(): Promise<void> {
    if (this.masterKey) return;

    this.ensureDir();
    this.salt = this.loadOrCreateSalt();

    // Determine strategy
    const strategyPath = path.join(this.secretsDir, ".key-strategy");
    if (fs.existsSync(strategyPath)) {
      this.strategy = fs.readFileSync(strategyPath, "utf-8").trim() as KeyStrategy;
    } else {
      // Auto-detect: try keychain, fall back to machine
      this.strategy = (await this.tryKeychainStrategy()) ? "keychain" : "machine";
      fs.writeFileSync(strategyPath, this.strategy, { mode: 0o600 });
    }

    // Derive key from strategy
    const passphrase = await this.getPassphraseForStrategy(this.strategy);
    this.masterKey = deriveMasterKey(passphrase, this.salt);
  }

  private async tryKeychainStrategy(): Promise<boolean> {
    try {
      // `keytar` is an optional OS-keychain native module the user installs
      // themselves; the `as string` specifier keeps it out of tsc resolution.
      const keytar = await import("keytar" as string);
      let stored = await keytar.getPassword(KEYCHAIN_SERVICE, KEYCHAIN_ACCOUNT);
      if (!stored) {
        stored = crypto.randomBytes(32).toString("base64");
        await keytar.setPassword(KEYCHAIN_SERVICE, KEYCHAIN_ACCOUNT, stored);
      }
      return true;
    } catch {
      return false;
    }
  }

  private async getPassphraseForStrategy(strategy: KeyStrategy): Promise<string> {
    switch (strategy) {
      case "keychain": {
        try {
          // `keytar` is an optional OS-keychain native module the user installs
          // themselves; the `as string` specifier keeps it out of tsc resolution.
          const keytar = await import("keytar" as string);
          const stored = await keytar.getPassword(KEYCHAIN_SERVICE, KEYCHAIN_ACCOUNT);
          if (stored) return stored;
        } catch {
          // Keychain failed; fall through to machine
        }
        return getMachinePassphrase();
      }
      case "passphrase": {
        const envPassphrase = process.env[PASSPHRASE_ENV];
        if (envPassphrase) {
          // Verify against stored hash if it exists
          const masterPath = path.join(this.secretsDir, ".master");
          if (fs.existsSync(masterPath)) {
            const storedHash = fs.readFileSync(masterPath, "utf-8").trim();
            if (hashPassphrase(envPassphrase) !== storedHash) {
              throw new SecretsError(
                `Passphrase from ${PASSPHRASE_ENV} does not match stored hash. ` +
                  "Use skaile auth set-key-strategy passphrase to reset.",
                "PASSPHRASE_MISMATCH",
              );
            }
          }
          return envPassphrase;
        }
        // No env var -- hard error. Silent fallback to machine would
        // produce a different key, causing AES-GCM auth-tag failures
        // on secrets encrypted with the real passphrase.
        throw new SecretsError(
          `Strategy is "passphrase" but ${PASSPHRASE_ENV} is not set. ` +
            `Export it before running: export ${PASSPHRASE_ENV}='...'`,
          "PASSPHRASE_REQUIRED",
        );
      }
      case "machine":
        return getMachinePassphrase();
      default:
        return getMachinePassphrase();
    }
  }

  // -- Strategy management ---------------------------------------------------

  /**
   * Set the key strategy explicitly. Called by `skaile auth set-key-strategy`.
   *
   * When existing secrets are present, they are re-encrypted with the new key
   * to prevent data loss from key mismatch.
   */
  async setKeyStrategy(strategy: KeyStrategy, passphrase?: string): Promise<void> {
    this.ensureDir();

    // --- 1. Validate new strategy prerequisites -------------------------

    const effectivePassphrase = passphrase ?? process.env[PASSPHRASE_ENV];

    if (strategy === "passphrase") {
      if (!effectivePassphrase) {
        throw new SecretsError(
          `Passphrase required. Pass it as an argument or set ${PASSPHRASE_ENV}.`,
          "PASSPHRASE_REQUIRED",
        );
      }
    }

    if (strategy === "keychain") {
      const success = await this.tryKeychainStrategy();
      if (!success) {
        throw new ProviderUnavailableError("local", "OS keychain not available");
      }
    }

    // --- 2. Load the OLD key under the current strategy -----------------
    // ensureKey() reads the current strategy file and derives the key
    // that was used to encrypt existing secrets. This is critical for
    // cross-process scenarios where masterKey starts as null.

    const encFiles = this.listEncFiles();
    if (encFiles.length > 0) {
      await this.ensureKey();
      const oldKey = this.masterKey!;

      // Derive the new key
      const salt = this.salt!;
      const newPassphrase =
        strategy === "passphrase"
          ? effectivePassphrase!
          : strategy === "keychain"
            ? await this.getPassphraseForStrategy("keychain")
            : getMachinePassphrase();
      const newKey = deriveMasterKey(newPassphrase, salt);

      // Re-encrypt each secret with the new key. A single failure must abort the
      // whole switch: committing the new strategy while a secret is still under
      // the old key orphans that secret (the registry advertises a key that no
      // longer decrypts it). Collect failures and throw before the commit below,
      // leaving `.key-strategy`/`.master` — and every secret — on the old key.
      const failed: string[] = [];
      for (const encFile of encFiles) {
        const filePath = path.join(this.secretsDir, encFile);
        const blob = fs.readFileSync(filePath);
        try {
          const plaintext = decrypt(blob, oldKey);
          const reEncrypted = encrypt(plaintext, newKey);
          fs.writeFileSync(filePath, reEncrypted, { mode: 0o600 });
        } catch {
          failed.push(encFile);
        }
      }
      if (failed.length > 0) {
        throw new Error(
          `[skaile/secrets] Key-strategy switch aborted: could not re-encrypt ` +
            `${failed.length} secret(s) (${failed.join(", ")}). The previous strategy ` +
            "is unchanged; resolve the failing secrets before retrying.",
        );
      }
    }

    // --- 3. Commit the new strategy (last write -- failure above leaves
    //        the old strategy intact) ------------------------------------

    if (strategy === "passphrase") {
      const masterPath = path.join(this.secretsDir, ".master");
      fs.writeFileSync(masterPath, hashPassphrase(effectivePassphrase!), { mode: 0o600 });
    }

    const strategyPath = path.join(this.secretsDir, ".key-strategy");
    fs.writeFileSync(strategyPath, strategy, { mode: 0o600 });
    this.masterKey = null;
    this.strategy = strategy;
  }

  getKeyStrategy(): KeyStrategy {
    const strategyPath = path.join(this.secretsDir, ".key-strategy");
    if (fs.existsSync(strategyPath)) {
      return fs.readFileSync(strategyPath, "utf-8").trim() as KeyStrategy;
    }
    return "machine";
  }

  // -- File helpers ----------------------------------------------------------

  private encFilename(ref: string): string {
    return `${refToHash(ref)}.enc`;
  }

  private metaFilename(ref: string): string {
    return `${refToHash(ref)}.meta.json`;
  }

  private metaPath(ref: string): string {
    return path.join(this.secretsDir, this.metaFilename(ref));
  }

  private listEncFiles(): string[] {
    if (!fs.existsSync(this.secretsDir)) return [];
    return fs.readdirSync(this.secretsDir).filter((f) => f.endsWith(".enc"));
  }

  // -- ISecretsProvider implementation ----------------------------------------

  async getSecret(ref: string): Promise<Uint8Array> {
    await this.ensureKey();
    const filePath = path.join(this.secretsDir, this.encFilename(ref));
    if (!fs.existsSync(filePath)) {
      throw new SecretNotFoundError(ref);
    }
    const blob = fs.readFileSync(filePath);
    const decrypted = decrypt(blob, this.masterKey!);

    // Update lastAccessedAt
    const metaFile = this.metaPath(ref);
    if (fs.existsSync(metaFile)) {
      try {
        const meta = JSON.parse(fs.readFileSync(metaFile, "utf-8"));
        meta.lastAccessedAt = new Date().toISOString();
        fs.writeFileSync(metaFile, JSON.stringify(meta), { mode: 0o600 });
      } catch {
        // Non-fatal
      }
    }

    return new Uint8Array(decrypted);
  }

  async setSecret(ref: string, value: Uint8Array, opts?: SetSecretOptions): Promise<void> {
    await this.ensureKey();
    this.ensureDir();
    const filePath = path.join(this.secretsDir, this.encFilename(ref));
    const encrypted = encrypt(value, this.masterKey!);
    fs.writeFileSync(filePath, encrypted, { mode: 0o600 });

    // Write metadata (stores the original ref for reverse lookup)
    const now = new Date().toISOString();
    const metaFile = this.metaPath(ref);
    let existing: Record<string, unknown> = {};
    if (fs.existsSync(metaFile)) {
      try {
        existing = JSON.parse(fs.readFileSync(metaFile, "utf-8"));
      } catch {
        // Overwrite corrupt meta
      }
    }
    const meta = {
      ref,
      createdAt: existing.createdAt ?? now,
      updatedAt: now,
      lastAccessedAt: now,
      ...(opts?.metadata ? { userMeta: opts.metadata } : {}),
      ...(opts?.ttlSeconds
        ? { expiresAt: new Date(Date.now() + opts.ttlSeconds * 1000).toISOString() }
        : {}),
    };
    fs.writeFileSync(metaFile, JSON.stringify(meta, null, 2), { mode: 0o600 });
  }

  async deleteSecret(ref: string): Promise<void> {
    const filePath = path.join(this.secretsDir, this.encFilename(ref));
    const metaFile = this.metaPath(ref);
    if (!fs.existsSync(filePath)) {
      throw new SecretNotFoundError(ref);
    }
    fs.unlinkSync(filePath);
    if (fs.existsSync(metaFile)) {
      fs.unlinkSync(metaFile);
    }
  }

  async listSecrets(filter?: ListFilter): Promise<string[]> {
    this.ensureDir();
    // Read refs from meta files (the .enc filenames are hashes, not refs)
    const metaFiles = fs.readdirSync(this.secretsDir).filter((f) => f.endsWith(".meta.json"));
    let refs: string[] = [];
    for (const mf of metaFiles) {
      try {
        const meta = JSON.parse(fs.readFileSync(path.join(this.secretsDir, mf), "utf-8"));
        if (meta.ref) refs.push(meta.ref);
      } catch {
        // Skip corrupt meta files
      }
    }
    if (filter?.prefix) {
      refs = refs.filter((r) => r.startsWith(filter.prefix!));
    }
    if (filter?.limit) {
      refs = refs.slice(0, filter.limit);
    }
    return refs;
  }

  async testConnection(): Promise<{ ok: boolean; latencyMs?: number; error?: string }> {
    const start = Date.now();
    try {
      await this.ensureKey();
      // Write + read + delete a test secret
      const testRef = `__test_${crypto.randomBytes(4).toString("hex")}`;
      const testValue = new TextEncoder().encode("test");
      await this.setSecret(testRef, testValue);
      const read = await this.getSecret(testRef);
      await this.deleteSecret(testRef);
      if (new TextDecoder().decode(read) !== "test") {
        return { ok: false, error: "Round-trip mismatch" };
      }
      return { ok: true, latencyMs: Date.now() - start };
    } catch (e) {
      return {
        ok: false,
        latencyMs: Date.now() - start,
        error: e instanceof Error ? e.message : String(e),
      };
    }
  }

  async getMetadata(ref: string): Promise<SecretMetadata> {
    const metaFile = this.metaPath(ref);
    const filePath = path.join(this.secretsDir, this.encFilename(ref));
    if (!fs.existsSync(filePath)) {
      throw new SecretNotFoundError(ref);
    }
    if (fs.existsSync(metaFile)) {
      const raw = JSON.parse(fs.readFileSync(metaFile, "utf-8"));
      return {
        ref: raw.ref ?? ref,
        createdAt: new Date(raw.createdAt),
        updatedAt: new Date(raw.updatedAt),
        lastAccessedAt: raw.lastAccessedAt ? new Date(raw.lastAccessedAt) : undefined,
        owner: raw.owner,
        scopeSummary: "local encrypted file",
      };
    }
    // Fallback: derive from file stat
    const stat = fs.statSync(filePath);
    return {
      ref,
      createdAt: stat.birthtime,
      updatedAt: stat.mtime,
      scopeSummary: "local encrypted file",
    };
  }

  getCapabilities(): ProviderCapabilities {
    return {
      supportsRotation: false,
      supportsExpiry: false,
      supportsBatch: false,
      scopeModel: "flat",
    };
  }
}
